import { forwardRef } from "react";
import { Crescent } from "./crescent";
import { Icon } from "./icon";
import { cx } from "@/utils/cx";
import type { IconName } from "./icon";
import type { ButtonHTMLAttributes, Ref } from "react";

/**
 * Gymkee 2027 icon-only control (spec icon-button 1.5; sheet DS02).
 *
 * The 40 or 48 square in the hairline or the ghost look, with the 20 or 24 glyph inside. Never
 * orange, never a circle, and never without a spoken name: an icon alone is silent to a screen
 * reader and nameless under the pointer, so `label` is required on the type here rather than
 * warned about at runtime.
 */

export interface IconButtonProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "children"
> {
  icon: IconName;
  /** The spoken name, from an i18n key: a verb, or the thing it opens. */
  label: string;
  /** The hairline square (default) or the bare ghost; an icon control is never a fill. */
  variant?: "tertiary" | "ghost";
  /** medium is 40 with a pointer, large is 48 for the hand. */
  size?: "medium" | "large";
  loading?: boolean;
  href?: string;
  target?: string;
  className?: string;
}

const GLYPH = { medium: 20, large: 24 } as const;

export const IconButton = forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  IconButtonProps
>(
  (
    {
      icon,
      label,
      variant = "tertiary",
      size = "medium",
      loading,
      href,
      target,
      className,
      disabled: _disabled,
      ...props
    },
    ref,
  ) => {
    const disabled = !!_disabled || !!loading;
    const classes = cx(
      "ds-icon-btn",
      variant === "ghost" ? "ds-btn-ghost" : "ds-btn-tertiary",
      { "ds-btn-lg": size === "large", "pointer-events-none": disabled },
      className,
    );
    const body = loading ? (
      <Crescent size={16} tone="tertiary" delay={0} />
    ) : (
      <Icon name={icon} size={GLYPH[size]} />
    );

    if (href) {
      return (
        <a
          ref={ref as Ref<HTMLAnchorElement>}
          href={href}
          target={target}
          rel={target === "_blank" ? "noopener noreferrer" : undefined}
          className={classes}
          title={label}
          aria-label={label}
        >
          {body}
        </a>
      );
    }

    return (
      <button
        ref={ref as Ref<HTMLButtonElement>}
        type="button"
        disabled={disabled}
        aria-busy={loading || undefined}
        className={classes}
        title={label}
        aria-label={label}
        {...props}
      >
        {body}
      </button>
    );
  },
);

IconButton.displayName = "IconButton";
